/**
 * Invite-link parsing for the `/join` page (#1720).
 *
 * People do not paste tidy tokens. They paste the whole thing out of
 * the invite email — `https://tarii-time.com/invite/<token>`, sometimes
 * with the `/accept` suffix a signed-in hop leaves behind, sometimes
 * with a tracking query string, sometimes wrapped in whitespace by the
 * mail client. Occasionally they paste just the code. All of those are
 * the same intent, and `/join` accepts all of them rather than telling
 * someone their own invite is wrong.
 *
 * Tokens are minted server-side as `secrets.token_urlsafe(32)`
 * (`backend/src/api/routes/members/invites.py:126`) — 43 characters of
 * the URL-safe base64 alphabet. We validate the SHAPE only, generously
 * (16–256 of `[A-Za-z0-9_-]`), and let the backend be the authority on
 * whether the token is real: the honest failure for an expired or
 * already-used invite belongs on the preview page, which says so
 * calmly. The shape check exists to catch "hello" and a pasted email
 * address before we navigate somewhere that will 404.
 */

/** Server tokens are 43 chars; the window is deliberately generous. */
const TOKEN_SHAPE = /^[A-Za-z0-9_-]{16,256}$/;

/** Matched WITHOUT a leading slash so `invite/<token>` (a link the
 *  mail client stripped the origin off) parses too. */
const INVITE_SEGMENT = "invite/";

/**
 * Pull the invite token out of whatever the person pasted.
 *
 * @returns the bare token, or `null` when the input is not an invite
 *   link or a plausible token.
 */
export function parseInviteTokenInput(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  let candidate = trimmed;

  // A link (absolute, protocol-relative, or a bare `/invite/...` path).
  // Take the LAST `/invite/` so a copy-paste that concatenated two
  // links resolves to the one the person most likely meant — the tail.
  const segmentAt = trimmed.lastIndexOf(INVITE_SEGMENT);
  if (segmentAt !== -1) {
    candidate = trimmed.slice(segmentAt + INVITE_SEGMENT.length);
  } else if (/[/:?#]/.test(trimmed)) {
    // Has URL punctuation but no `/invite/` segment — a link to
    // somewhere else entirely. Don't guess.
    return null;
  }

  // Cut anything trailing the token: the `/accept` suffix, a query
  // string, a fragment.
  candidate = candidate.split(/[/?#]/, 1)[0] ?? "";

  // Mail clients percent-encode aggressively; the token alphabet is
  // already URL-safe, so a decode is lossless when it succeeds and
  // ignorable when it doesn't.
  try {
    candidate = decodeURIComponent(candidate);
  } catch {
    /* malformed escape — fall through with the raw candidate */
  }

  return TOKEN_SHAPE.test(candidate) ? candidate : null;
}
